import { useCallback, useState } from "react";

/**
 * Expandable per-day reminder breakdown for the History and Member-profile tables. Unlike the
 * break breakdown, the counts already arrive inline on each attendance row (row.reminderCount +
 * row.reminderBreakdown from the API), so this is a pure toggle — no fetch. One row open at a
 * time; the key is caller-supplied since History rows (incl. absent placeholders) have no id.
 */
export const useReminderDetails = () => {
  const [openKey, setOpenKey] = useState(null);
  const isOpen = useCallback((key) => openKey != null && String(openKey) === String(key), [openKey]);
  const toggle = useCallback((key) => setOpenKey((cur) => (String(cur) === String(key) ? null : key)), []);
  return { isOpen, toggle };
};

// reminder_log.context -> admin-facing label, in display order.
const CONTEXTS = [
  ["not_signed_in", "Sign-in"],
  ["on_break", "Long break"],
  ["missing_checkout", "Sign-out"],
  ["abandon_warning", "Abandon warning"],
];

/**
 * The "Reminders" table cell: a ×N count chip plus the toggle that opens the type breakdown.
 * Shows a plain dash when no reminder was sent that day.
 */
export const ReminderCell = ({ count, rowKey, expanded, onToggle }) => {
  if (!count) return <span style={{ color: "var(--muted-2)" }}>—</span>;
  return (
    <div className="log-remind">
      <span className="brk-chip">×{count}</span>
      <button
        type="button"
        className={`brk-toggle ${expanded ? "on" : ""}`}
        title={expanded ? "Hide reminder types" : "Show reminder types"}
        aria-expanded={expanded}
        onClick={(e) => {
          e.stopPropagation(); // rows themselves navigate to the profile
          onToggle(rowKey);
        }}
      >
        <svg>
          <use href="#i-chev-r" />
        </svg>
      </button>
    </div>
  );
};

/**
 * Full-width panel rendered right under an expanded row: the count of reminders sent that day
 * broken down by type (sign-in / long-break / sign-out / abandon). Auto and manual sends combined.
 */
export const ReminderDetailRow = ({ breakdown }) => {
  const entries = CONTEXTS.filter(([ctx]) => (breakdown?.[ctx] || 0) > 0);
  if (!entries.length) return <div className="brk-detail muted">No reminders for this day.</div>;
  const total = entries.reduce((a, [ctx]) => a + breakdown[ctx], 0);
  return (
    <div className="brk-detail">
      <div className="brk-detail-head">Reminders ({total})</div>
      {entries.map(([ctx, label]) => (
        <div className="brk-detail-row" key={ctx}>
          <span className="brk-lbl">{label}</span>
          <span className="brk-dur">×{breakdown[ctx]}</span>
        </div>
      ))}
    </div>
  );
};
